import { useEffect, useState } from 'react';

import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Drawer,
  Popconfirm,
  Space,
  Spin,
  Tabs,
  Tooltip,
  Typography,
  message,
} from 'antd';

import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { KassaService } from 'services/KassaService';
import { checkField } from 'utils';
import { kassaStatusesMap } from 'utils/handbooks/kassaHandbooks';

import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { PasswordHide } from 'components/PasswordHide';
import {
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { Center } from 'ui/Center';
import { RowBlock } from 'ui/RowBlock';
import { SectionTitle } from 'ui/SectionTitle';
import { KassaForm } from './KassaForm';
import { KassaTable } from './KassaTable';
import { ShiftsTable } from './ShiftsTable';

const PAGE_TITLE = 'Касса';

function Kassa() {
  useDocumentTitle(PAGE_TITLE);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const [kassas, setKassas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kassaInfo, setKassaInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('kassa');
  const [{ record, recordType }, setRecordInfo] = useState({
    record: null,
    recordType: null,
  });
  const [passwordOpen, setPasswordOpen] = useState({
    ofd: false,
    partner: false,
  });
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    status: null,
  });

  const { client_id: selectedClient } = filters;

  const recordTitle =
    recordType === 'shifts'
      ? `Смена № ${record?.number}`
      : `Чек № ${record?.fixed_check}`;

  useEffect(() => {
    if (activeRecord) {
      fetchClientKassa();
    } else {
      setKassaInfo(null);
      setActiveTab('kassa');
    }
  }, [activeRecord]);

  useEffect(() => {
    fetchKassas();
  }, [page, perPage, filters]);

  useEffect(() => {
    if (clientId) {
      handleClientSelect(clientId);
    }
  }, []);

  const fetchKassas = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await KassaService.getKassas(
        page,
        perPage,
        filters
      );

      setKassas(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientKassa = async () => {
    setLoading(true);

    try {
      const info = await KassaService.getClientKassaInfo(activeRecord?.id);

      setKassaInfo(info);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await KassaService.deleteKassa(activeRecord.id);

      if (isDeleted) {
        closeDrawer();
        fetchKassas();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const handleDownload = async () => {
    const hide = message.loading(REQUEST_LOADING);

    const recordId = record.id;
    const kassaId = activeRecord?.id;

    try {
      const bufferedResponse =
        recordType === 'shifts'
          ? await KassaService.downloadShift(kassaId, recordId)
          : await KassaService.downloadCheck(kassaId, recordId);

      const blob = new Blob([bufferedResponse], { type: 'application/pdf' });
      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);

      link.href = blobUrl;
      link.download = `${recordTitle}.pdf`;

      link.click();
      link.remove();
    } catch (err) {
      message.error('Не удалось скачать PDF файл');
    } finally {
      hide();
    }
  };

  const openPreviewDrawer = (record, recordType) => {
    setRecordInfo({ record, recordType });
  };

  const closePreviewDrawer = () => {
    setRecordInfo({ record: null, recordType: null });
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchKassas}>
            Обновить
          </Button>
          <AccessControl pageId="kassas" action="create">
            <Button
              disabled={!selectedClient}
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateForm}
            >
              Создать
            </Button>
          </AccessControl>
        </Space>
      </ActionsWrapper>

      <KassaTable
        allowFilter
        items={kassas}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <Drawer
        title={activeRecord?.client}
        width="50%"
        visible={drawerVisible}
        onClose={closeDrawer}
        extra={
          <Space>
            <AccessControl pageId="kassas" action="update">
              <Space>
                <Tooltip placement="bottomLeft" title="Редактировать кассу">
                  <Button
                    type="default"
                    onClick={openEditForm}
                    icon={<EditOutlined />}
                  />
                </Tooltip>
              </Space>
            </AccessControl>
            <AccessControl pageId="kassas" action="delete">
              <Popconfirm
                title="Вы точно хотите удалить эту кассу ?"
                placement="bottomLeft"
                onConfirm={handleDelete}
                okText="Да"
                cancelText="Нет"
              >
                <Tooltip placement="bottomLeft" title="Удалить кассу">
                  <Button type="default" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </AccessControl>
          </Space>
        }
      >
        <Tabs
          centered
          activeKey={activeTab}
          onChange={(tab) => setActiveTab(tab)}
        >
          <Tabs.TabPane tab="Касса" key="kassa">
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Spin />
              </div>
            ) : (
              <>
                <SectionTitle offset={12}>Статус кассы</SectionTitle>

                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Статус:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaStatusesMap[kassaInfo?.status])}
                  </Typography.Text>
                </RowBlock>

                <SectionTitle offset={12}>ОФД</SectionTitle>

                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Логин:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.ofd_credentials?.login)}
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Пароль:
                  </Typography.Text>
                  <Typography.Text
                    type="password"
                    className="column-block-left"
                  >
                    <PasswordHide
                      condition={passwordOpen.ofd}
                      text={kassaInfo?.ofd_credentials?.password}
                      setPasswordOpen={setPasswordOpen}
                      passwordOpen={passwordOpen}
                      conditionSet="ofd"
                    />
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Токен:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.ofd_credentials?.token)}
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Оператор:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.ofd_credentials?.ofd_operator)}
                  </Typography.Text>
                </RowBlock>

                <SectionTitle offset={12}>Адрес торговой точки</SectionTitle>

                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Город:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.city)}
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Район:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.region)}
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Улица:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.street)}
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Квартира:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.apartment)}
                  </Typography.Text>
                </RowBlock>

                <SectionTitle offset="12">Партнер - касса</SectionTitle>

                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Email адрес:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.kassa_credentials?.email)}
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Пароль:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    <PasswordHide
                      condition={passwordOpen.partner}
                      text={kassaInfo?.kassa_credentials?.password}
                      setPasswordOpen={setPasswordOpen}
                      passwordOpen={passwordOpen}
                      conditionSet="partner"
                    />
                  </Typography.Text>
                </RowBlock>
                <RowBlock>
                  <Typography.Text
                    type="secondary"
                    className="column-block-right"
                  >
                    Партнер:
                  </Typography.Text>
                  <Typography.Text className="column-block-left">
                    {checkField(kassaInfo?.kassa_credentials?.partner)}
                  </Typography.Text>
                </RowBlock>
              </>
            )}
          </Tabs.TabPane>

          {kassaInfo?.remote_kassa_id && (
            <Tabs.TabPane tab="Смены" key="shifts">
              <ShiftsTable
                kassaId={activeRecord?.id}
                onRowClick={openPreviewDrawer}
              />
            </Tabs.TabPane>
          )}
        </Tabs>

        {/* Multi drawer of shifts and chesk pdf preview */}

        <Drawer
          width="50%"
          title={recordTitle}
          visible={record}
          onClose={closePreviewDrawer}
          extra={
            <Tooltip placement="bottomLeft" title="Скачать PDF файл">
              <Button icon={<DownloadOutlined />} onClick={handleDownload} />
            </Tooltip>
          }
        >
          {record?.html_code ? (
            <iframe
              title={recordTitle}
              srcDoc={record?.html_code}
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <Center>
              <Alert
                message="По этой записи нет PDF файла"
                type="error"
                showIcon
              />
            </Center>
          )}
        </Drawer>
      </Drawer>

      <KassaForm
        clientId={selectedClient || activeRecord?.client_id}
        isActive={formIsActive}
        initialValues={kassaInfo}
        onClose={closeForm}
        onSuccess={fetchKassas}
      />
    </>
  );
}

export { Kassa };
